/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';

import {ADN_GRIDSTER_CONFIG} from '../config/gridster-config';
import {WIDGET_TYPE} from '../common/constants/widget-type';
import {Uuid} from '../../components/util/uuid';
import {
  apiInstantFromPickerDate,
  apiInstantFromPickerDateBoundary,
  pickerDateFromApiDay,
  pickerDateFromApiInstant
} from '../common/locale/network-date-picker-utils';

export function setupWorkspaceWidgetDefaults(
  widgets: any[],
  translateFn: (key: string) => string,
  localNetworkProfile: any,
  adnTimeShifter: any
): void {
  const timeZone = localNetworkProfile.get('timeZone') || 'utc';
  _.forEach(widgets, (widget) => {
    widget.systemDefined = _.cloneDeep(WIDGET_TYPE[widget.systemDefined.id].systemDefined);
    widget.fields = widget.fields || _.cloneDeep(WIDGET_TYPE[widget.systemDefined.id].fields);
    widget.fields = _.map(widget.fields, (f) => f.id || f);
    widget.totalsType = widget.totalsType || 'HIDE';
    widget.fieldComparisons = widget.systemDefined.fieldComparison && !_.get(widget, ['fieldComparisons', 'length'])
      ? [{field: 'rendered', target: undefined}]
      : widget.fieldComparisons;
    widget.startDate = (_.has(widget, 'startDate') && !widget.startDate)
      ? moment.tz(timeZone).startOf('year').startOf('day').toDate()
      : widget.startDate;
    widget.modStartDate = pickerDateFromApiDay(widget.startDate, adnTimeShifter, 'start');
    widget.endDate = _.has(widget, 'endDate') && !widget.endDate
      ? moment.tz(timeZone).endOf('year').endOf('day').toDate()
      : widget.endDate;
    widget.modEndDate = pickerDateFromApiDay(widget.endDate, adnTimeShifter, 'end');
    widget.dateOptionStartDate = (widget.systemDefined.hasChartDates && !widget.dateOptionStartDate)
      ? moment.tz(timeZone).subtract(7, 'd').startOf('day').toDate()
      : widget.dateOptionStartDate;
    widget.modDateOptionStartDate = widget.dateOptionStartDate
      ? pickerDateFromApiInstant(widget.dateOptionStartDate, adnTimeShifter)
      : undefined;
    widget.dateOptionEndDate = (widget.systemDefined.hasChartDates && !widget.dateOptionEndDate)
      ? moment.tz(timeZone).toDate()
      : widget.dateOptionEndDate;
    widget.modDateOptionEndDate = widget.dateOptionEndDate
      ? pickerDateFromApiInstant(widget.dateOptionEndDate, adnTimeShifter)
      : undefined;
    widget.rows = Math.min((_.isFinite(widget.rows) && widget.rows > 0 ? widget.rows : 10), 100);
    _.forEach(widget.systemDefined, (value, key) => {
      widget.excludeValues = widget.excludeValues || {};
      if (!Object.prototype.hasOwnProperty.call(widget.excludeValues, key)) {
        widget.excludeValues[key] = value.value;
      }
    });

    const doTrans = (f: any) => {
      f.label = translateFn((widget.systemDefined.statFields ? 'report.field.' : 'label.') + f.id);
      const trans = localNetworkProfile.getNetworkTranslations(f.transKey || f.id);
      if (trans) {
        f.labelInstant = trans;
      }
    };

    _.forEach(widget.systemDefined.allFields, doTrans);
    _.forEach(widget.systemDefined.statFields, doTrans);
    _.forEach(widget.systemDefined.pubFields, doTrans);
    _.forEach(widget.systemDefined.advFields, doTrans);
  });
}

export function prepareWorkspaceWidgetsForSubmit(
  widgets: any[],
  adnTimeShifter: any
): void {
  _.forEach(widgets, (widget) => {
    const unorderedFields = _.cloneDeep(widget.fields);
    widget.fields = [];
    _.forEach(widget.systemDefined.allFields || widget.systemDefined.statFields, (f: any) => {
      if (_.indexOf(unorderedFields, f.id) > -1) {
        widget.fields.push(f.id);
      }
    });
    widget.fieldComparisons = _.filter(widget.fieldComparisons, (cf: any) => cf.field && cf.target);
    widget.obj = _.pick(widget.obj, ['id', 'name']);
    widget.objMulti = _.map(widget.objMulti, (om: any) => ({id: om.id, name: om.name}));

    widget.startDate = apiInstantFromPickerDateBoundary(widget.modStartDate, adnTimeShifter, 'start');
    widget.dateOptionStartDate = _.isDate(widget.modDateOptionStartDate)
      ? apiInstantFromPickerDate(widget.modDateOptionStartDate, adnTimeShifter)
      : null;
    widget.endDate = apiInstantFromPickerDateBoundary(widget.modEndDate, adnTimeShifter, 'end');
    widget.dateOptionEndDate = _.isDate(widget.modDateOptionEndDate)
      ? apiInstantFromPickerDate(widget.modDateOptionEndDate, adnTimeShifter)
      : null;

    if (!widget.systemDefined.hasChartDates) {
      widget.dateOptionStartDate = null;
      widget.dateOptionsEndDate = null;
    }
  });
}

export function buildWorkspaceGridsterOpts(editable: boolean, onLayoutChange?: () => void): any {
  const myOpts = _.cloneDeep(ADN_GRIDSTER_CONFIG.WORKSPACES);
  myOpts.draggable.enabled = editable;
  myOpts.resizable.enabled = editable;
  if (editable) {
    myOpts.emptyMinRows = 3;
  }
  const gridsterOpts = _.merge(myOpts, {
    resizable: {
      stop: () => onLayoutChange?.()
    },
    draggable: {
      stop: () => onLayoutChange?.()
    }
  });
  if (!editable) {
    myOpts.margins = [14, 14];
  }
  return gridsterOpts;
}

export function initWorkspaceModel(
  model: any,
  hasNetworkPermission: boolean,
  isPureMarketplacePlatform: boolean
): void {
  model.data = model.data || {};
  model.data.widgets = model.data.widgets || [];
  if (!model.type) {
    model.type = hasNetworkPermission && !isPureMarketplacePlatform ? 'NETWORK' : 'PERSONAL';
  }
}

export function workspaceWhichFields(fieldType: string, widget: any): boolean {
  if (widget.objectType === 'Creative' || widget.objectType === 'LineItem'
    || widget.objectType === 'Order' || widget.objectType === 'Advertiser') {
    return fieldType === 'advFields';
  }
  return fieldType === 'pubFields';
}

export function getAvailableWorkspaceWidgets(localUserPermissions: any): any[] {
  return _.filter(_.cloneDeep(WIDGET_TYPE), (w) => {
    if (!w.systemDefined.permissions) {
      return true;
    }
    const perms = _.filter(_.map(localUserPermissions.getAllNetworkPermissions(), (perm, key) => {
      return perm ? key : null;
    }), (p) => p);
    return _.intersection(perms, w.systemDefined.permissions).length > 0;
  });
}

export function getWorkspaceWidgetTags(widgets: any[]): Record<string, number> {
  const totalTags = _.flatten(_.map(widgets, (widget) => widget.systemDefined.tags));
  const uniqueCount: Record<string, number> = {};
  _.forEach(totalTags, (tag) => {
    uniqueCount[tag] = (uniqueCount[tag] || 0) + 1;
  });
  return uniqueCount;
}

export function addWorkspaceWidget(widgets: any[], widget: any): any {
  const newWidget = _.cloneDeep(widget);
  newWidget.id = Uuid.generate();
  widgets.push(newWidget);
  return newWidget;
}

export function removeWorkspaceWidget(widgets: any[], widget: any): void {
  _.remove(widgets, (savedWidget: any) => widget.id === savedWidget.id);
}

